import { useCallback, useState } from "react";

import {
    HELP_CONTENT,
    type HelpSection,
    type HelpSectionKey,
} from "@/lib/help-content";

/**
 * Custom hook to manage help dialog state.
 *
 * Provides a simple API to open/close help dialogs and access their content.
 * The content is fetched from the centralized HELP_CONTENT registry.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const help = useHelpDialog("priceComparison");
 *
 *   return (
 *     <>
 *       <Button onClick={help.open}>
 *         <HelpCircle />
 *       </Button>
 *       <HelpDialog
 *         content={help.content}
 *         open={help.isOpen}
 *         onOpenChange={help.setIsOpen}
 *       />
 *     </>
 *   );
 * }
 * ```
 *
 * @param sectionKey - Key of the help section from HELP_CONTENT
 * @returns Object with dialog state and control functions
 */
export function useHelpDialog(sectionKey: HelpSectionKey) {
    const [isOpen, setIsOpen] = useState(false);

    const content: HelpSection = HELP_CONTENT[sectionKey];

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

    return {
        /** Whether the dialog is currently open */
        isOpen,
        /** Set the open state directly */
        setIsOpen,
        /** Open the dialog */
        open,
        /** Close the dialog */
        close,
        /** Toggle the dialog open/closed */
        toggle,
        /** The help content for this section */
        content,
    };
}

/**
 * Return type of useHelpDialog hook
 */
export type UseHelpDialogReturn = ReturnType<typeof useHelpDialog>;
